import React from 'react';
import { ConfigProvider, ConfigProviderProps } from 'antd';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import jaJP from 'antd/locale/ja_JP';
import koKR from 'antd/locale/ko_KR';
// antd locale
import zhCN from 'antd/locale/zh_CN';
// 时间相关
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Helmet } from 'react-helmet';

// dayjs locale
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
// i18n
import '@/i18n';

import { useTranslation } from 'react-i18next';

import { LanguageEnum } from '@/i18n/config';

import './global.less';

import { Router } from '@/router';
import DingTalkLogin from '@/service/ding-talk-login';
import { overrideHistoryBack } from '@/utils/go-back';

dayjs.extend(utc);
dayjs.extend(timezone);

ConfigProvider.config({
  holderRender(children) {
    return (
      <ConfigProvider locale={zhCN} prefixCls="antd">
        {children}
      </ConfigProvider>
    );
  },
});

overrideHistoryBack();

// 初始化登录服务
DingTalkLogin.init();

/**
 * AI.GEN lines=1-20
 * App 组件 - 初始化登录服务并集成 i18n
 */
export const App = () => {
  const { i18n, t } = useTranslation();

  // 根据当前语言动态设置 antd locale 和 dayjs locale
  const getAntdLocale = () => {
    const language = i18n.language;
    switch (language) {
      case LanguageEnum.ZhCN:
        return zhCN;
      case LanguageEnum.EnUS:
        return enUS;
      case LanguageEnum.FrFR:
        return frFR;
      case LanguageEnum.JaJP:
        return jaJP;
      case LanguageEnum.KoKR:
        return koKR;
      default:
        return zhCN;
    }
  };

  // 设置 dayjs locale
  React.useEffect(() => {
    const language = i18n.language;
    let dayjsLocale = 'zh-cn';
    switch (language) {
      case LanguageEnum.ZhCN:
        dayjsLocale = 'zh-cn';
        break;
      case LanguageEnum.EnUS:
        dayjsLocale = 'en';
        break;
      case LanguageEnum.FrFR:
        dayjsLocale = 'fr';
        break;
      case LanguageEnum.JaJP:
        dayjsLocale = 'ja';
        break;
      case LanguageEnum.KoKR:
        dayjsLocale = 'ko';
        break;
    }
    dayjs.locale(dayjsLocale);
  }, [i18n.language]);

  const antdConfig: ConfigProviderProps = {
    locale: getAntdLocale(),
    prefixCls: 'antd',
    input: {
      allowClear: true,
    },
    textArea: {
      allowClear: true,
    },
    theme: {
      token: {
        fontSize: 14,
      },
      components: {
        Breadcrumb: {
          itemColor: '#FFF',
          lastItemColor: '#FFF',
          separatorColor: '#FFF',
        },
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>{t('app.title')}</title>
      </Helmet>
      <ConfigProvider {...antdConfig}>
        <Router></Router>
      </ConfigProvider>
    </>
  );
};
